import Emitter from "es6-event-emitter";

import { getBrowser } from "./../utilities/DOMUtils";
import {
  compact,
  generatePreview,
  stripHtml,
  areEventsTracked,
} from "./../utilities/utils";

const clientPackage = require(`./../../../clients/${process.env.CLIENT}/package.json`);
const clientConfig =
  require(`./../../../clients/${process.env.CLIENT}/js/analyticsConfig.js`).default;

/**
 * Appstate base handles the analytics
 */
export default class AppStateBase extends Emitter {
  /**
   * Content
   */
  // changed when cpStore is loaded again
  contentPackage = null;
  // Name
  // Label
  // Date
  // Id
  // ContentPackageType (Hors Serie, Free, ...)
  // Price
  // changed when pubStore is loaded again
  activePublication = null;
  // Name
  // Label
  // Date
  // Id
  // ExternalReference
  // PublicationType(Main, suplement)
  // PublicationFormat(Replica, NextGen)
  activePages = [];

  /**
   * User info
   */
  //only set once when a new SessionManager is created (in App)
  uuid = null; //done
  // set in the beginning of new SessionManager if in cookie,
  // if new session/old session is checked in sessionmanager this changes
  userId = null;
  // if new session/old session is checked in sessionmanager this changes
  sessionId = null;
  //changed when authenticated
  loginInformation = null; //done
  // subscriptionId
  // SubscriptionReference
  // UserSubscriptionReference
  // SubscriptionType
  defaultPagePercentageInView = 100;

  buildVersion = null;
  platformVersion = null;
  lastActivePageData = null;

  socialSharing = null;

  constructor(analyticsManager) {
    super();
    this.buildVersion = clientPackage.version;
    this.browserDetails = {
      navigatorName: getBrowser().name,
      navigatorVersion: getBrowser().version,
    };
    //TODO: create in app
    this.analyticsManager = analyticsManager;
  }

  triggerEvent(event, data = null, merge = false) {
    let eventData = this._getAnalyticAppState(event);
    if (data) {
      if (merge) {
        eventData = { ...eventData, ...data };
      } else {
        eventData = data;
      }
    }
    this.analyticsManager.handleEvent(event, eventData);
    if (areEventsTracked())
      console.log(event, this._getAnalyticAppState(event)); // eslint-disable-line
  }

  setPagesPercentageInView(singlePageMode) {
    this.defaultPagePercentageInView = singlePageMode ? 100 : 50;
  }

  /**
   * Sets pages that are currently active (viewed by user) in replica reader
   * @param {Array} data List of pages (one or two depending on view mode) with articles
   * @param {Object} percentageInView Object containing percentageInView for pages and articles
   */
  setActivePages(data, percentageInView = {}) {
    this.lastActivePageData = data;
    this.activePages = data.map((page) => {
      let item = null;

      if (page) {
        var publicationPageId = page.PublicationPageId
          ? page.PublicationPageId
          : page.PublicationPageID;
        item = this._createActivePage(
          page,
          percentageInView[`page_${publicationPageId}`]
        );
        item.articles = page.content.map((content) => {
          return this._createActiveArticle(
            content,
            percentageInView[`article_${content.ContentID}`]
          );
        });
      }
      return item;
    });
  }

  /**
   * Sets pages that are currently active (viewed by user) in nextgen reader. Data for pages and
   * articles can be setup in two ways:
   *
   * 1. generated based on page data from pubStore (this is used in bridge onPageAccess method)
   * 2. data are taken from ER SDK that sends correct object already, so we can just replace current object
   *
   * @param {Object}  nextgenPage nextgen page object
   * @param {Boolean} replace     should we assign nexgenPage to this.activePages as it is
   */
  setActiveNextgenPage(nextgenPage, replace = false) {
    if (replace) {
      this.activePages = nextgenPage;
    } else {
      const page = {
        pageTemplate: nextgenPage.PageTemplate.Name,
        pageReference: nextgenPage.PublicationPageId.toString(),
        pagePosition: nextgenPage.PageNumber,
        section: nextgenPage.PageCategory,
        thumbnailURL: `${generatePreview(
          "640/900",
          nextgenPage.PublicationPageId
        )}`,
        percentageInView: 100,
        properties: {},
        articles: this._createActiveNextgenArticles(
          nextgenPage.PageTemplate.Blocks,
          nextgenPage
        ),
      };

      this.activePages = [page];
    }
  }

  albClosed() {
    if (this.lastActivePageData) {
      this.setActivePages(this.lastActivePageData);
    }
  }

  /**
   * Sets active page and active article when ALB is rendered
   * @param {Object} page Active page
   * @param {Object} article Article shown in ALB
   */
  setActiveArticle(page, article) {
    const activePage = this._createActivePage(page, 100);
    activePage.articles = [this._createActiveArticle(article, 100)];
    this.activePages = [activePage];
  }

  setUserInfo(data) {
    const userReference = data[clientConfig.erUserReferenceType];
    this.subscriptionReference = data.subscriptionReference;
    this.userSubscriptionType = data.userSubscriptionType;

    this.userInfo = {
      userReference,
      type: userReference === null ? "anonymous" : "subscriber",
      subscriptionReference: this.subscriptionReference,
      properties: {},
    };
  }

  setContentPackage(data, cpDate, region, DownloadToken) {
    this.contentPackage = {
      bundleReference: data.ContentPackageId.toString(),
      bundleDateTime: `${cpDate}Z`,
      bundleName: data.ContentPackageName,
      editionName: region !== null ? region.Description : null,
      downloadToken: DownloadToken,
      productName: data.ProductName,
      properties: {},
    };
  }

  modifyContentPackage(data) {
    this.contentPackage.bundleReference = data.ContentPackageId.toString();
    this.contentPackage.bundleDateTime = `${data.PublicationDate}Z`;
  }

  ////////////////

  _createActiveNextgenArticles(blocks, page) {
    return blocks.reduce((acc, block) => {
      block.Contents.forEach((content) => {
        acc.push({
          articleReference:
            content.ExternalContentReference || stripHtml(content.Title),
          title: stripHtml(content.Title),
          author: content.Authors.length > 0 ? content.Authors[0].Name : null,
          percentageInView: 0,
          wordCount: 0,
          properties: {
            template: page.PageTemplate.Name,
            block: block.Name,
            blockPosition: content.ContentPosition,
            numberOfImages: content.Media.Images.length,
            numberOfVideos: content.Media.Videos.length,
          },
        });
      });

      return acc;
    }, []);
  }

  _getAnalyticAppState(event) {
    return {
      eventOrigin: event,
      eventData: {
        bundle: this._getBundleInfo(),
      },
      user: this.userInfo,
      deviceContext: {
        app: this._getAppInfo(),
        appContext: {
          socialSharing: this.socialSharing,
        },
        device: {
          deviceType: "WebApp",
          os: window.navigator.platform,
          screenWidth: window.innerWidth,
          screenHeight: window.innerHeight,
          ...this.browserDetails,
        },
      },
    };
  }

  _getBundleInfo() {
    return {
      ...this.contentPackage,
      publication: {
        publicationReference: this.activePublication
          ? this.activePublication.Id
          : null,
        publicationName: this.activePublication
          ? this.activePublication.Name
          : null,
        format: this.activePublication
          ? this.activePublication.PublicationFormat
          : null,
        type: this.activePublication
          ? this.activePublication.PublicationType
          : null,
        properties: {},
        pages: compact(this.activePages),
      },
    };
  }

  _getAppInfo() {
    return {
      appVersion: this.buildVersion,
      appName: `${clientPackage.client_fullname} - webApp`,
    };
  }

  _createActivePage(page, percentageInView) {
    var publicationPageId = page.PublicationPageId
      ? page.PublicationPageId
      : page.PublicationPageID;
    const activePage = {
      articles: page.articles,
      pageReference: page.ExternalPageReference,
      pagePosition: page.PageNumber,
      section: page.PageCategory,
      percentageInView:
        percentageInView === undefined
          ? this.defaultPagePercentageInView
          : percentageInView,
      properties: {},
      thumbnailURL: `${generatePreview("240/440", publicationPageId)}`,
      //create thumbnailURL since noSecuredThumbUrl is onlyplaced after opening of bottomNavigation
      // thumbnailURL: page.noSecuredThumbUrl,
    };
    return activePage;
  }

  _createActiveArticle(content, percentageInView) {
    const article = {
      author:
        content &&
        content.contentItemContent() &&
        content.contentItemContent().author
          ? content.contentItemContent().author.Author
          : "",
      articleReference: content.ExternalContentReference,
      title:
        content && content.contentItemContent()
          ? content.contentItemContent().title
          : "",
      wordCount:
        content && content.contentItemContent()
          ? content.contentItemContent().wordCount
          : 0,
      percentageInView: this._calculateArticlePercentageInView(
        content,
        percentageInView
      ),
      properties: {
        numberOfImages:
          content && content.contentItemContent()
            ? content.contentItemContent().images.length
            : 0,
        surfaceAreaPercentage: content.defaultPercentageInView,
      },
    };
    return article;
  }

  _calculateArticlePercentageInView(content, passedPercentageInView) {
    if (passedPercentageInView === undefined) {
      return (
        (content.defaultPercentageInView * this.defaultPagePercentageInView) /
        100
      );
    } else {
      return passedPercentageInView;
    }
  }

  set activePublication(data) {
    if (data && data.PublicationFormat) {
      data.PublicationFormat =
        data.PublicationFormat === "TabletOptimized" ? "Nextgen" : "Replica";
    }
    this._activePublication = data;
  }

  get activePublication() {
    return this._activePublication;
  }
}



// WEBPACK FOOTER //
// ./src/common/analytics/appStateBase.js